import "react-datepicker/dist/react-datepicker.css";

import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider
} from '@mui/material/styles';
import { FC, useEffect, useRef } from 'react';

import { useAppSelector } from './app/hooks';
import Router from './components/pages/Router/Router';
import LoadingProvider from './components/ui/LoadingProvider/LoadingProvider';
import ModalProvider from './components/ui/ModalProvider/ModalProvider';
import SnackbarProvider from './components/ui/SnackbarProvider/SnackbarProvider';
import { Theme } from './store/models/Auth';
import { colors } from './utils/colors';
import useGaTracker from './utils/googleAnalytics';

const createPageTheme = () =>
  createTheme({
    palette: {
      primary: {
        main: colors()?.colorFumisGreen1,
        dark: colors()?.colorFumisGreen1,
      },
      error: {
        main: colors()?.colorRed,
        dark: colors()?.colorRed,
      },
      warning: {
        main: colors()?.colorYellow,
        dark: colors()?.colorYellow,
      },
      secondary: {
        main: colors()?.colorBlack,
        dark: colors()?.colorBlack,
      },
    },
    typography: {
      fontFamily: [
        '"Open Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        'Helvetica',
        'Arial',
        'sans-serif',
      ].join(','),
    },
  });

const App: FC = () => {
  useGaTracker();
  const muiTheme = useRef(createPageTheme());

  const loginSuccess = useAppSelector((state) => state.auth.loginSuccess);
  const authCheck = useAppSelector((state) => state.auth.authCheck);
  const user = useAppSelector((state) => state.auth.user);

  //Only added for presentation purposes - TO-DO remove later
  useEffect(() => {
    const root = document?.getElementById('root');
    const html = document?.documentElement;
    if (root && html) {
      if (
        loginSuccess &&
        authCheck?.some((c) => c?.eMail?.endsWith('@jotul.com'))
      ) {
        root.style.backgroundImage = 'url(/background.jpg)';
        root.style.backgroundSize = 'cover';
        html.classList.add('dark');
      } else {
        root.style.backgroundImage = '';
        root.style.backgroundSize = '';
        html.classList.remove('dark');
      }
    }
    // eslint-disable-next-line
  }, [loginSuccess]);

  useEffect(() => {
    const html = document.documentElement;
    const theme = user?.settings?.theme;
    const themes = Object.values(Theme);
    themes.map((v) => html?.classList?.remove?.(v));
    if (theme) {
      html.classList.add(theme);
      muiTheme.current = createPageTheme();
    }
    return () => {
      themes.map((v) => html?.classList?.remove?.(v));
    };
  }, [user]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={muiTheme?.current}>
        <LoadingProvider>
          <ModalProvider>
            <SnackbarProvider>
              <Router />
            </SnackbarProvider>
          </ModalProvider>
        </LoadingProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
